import clsx from "clsx";
import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import Loader from "./Loader";

export type ConfirmTone = "primary" | "danger";

type Props = {
  title: string;
  /** One or two lines saying what actually happens, and whether it can be undone. */
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * The single confirmation dialog used across the app.
 *
 * One component rather than a copy per action, so every destructive prompt has the
 * same shape, the same escape hatches, and the same wording contract: a plain-ink
 * question, a sentence describing the consequence, and a button labelled with the
 * verb it performs ("Archive") rather than a generic "Submit" — a labelled verb is
 * what stops someone confirming the wrong thing on autopilot.
 */
export default function ConfirmDialog({
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
  loading = false,
  onConfirm,
  onCancel,
}: Props) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [loading, onCancel]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="z-[99999] fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-center justify-center p-4 animate-scrim-in"
      onClick={() => !loading && onCancel()}
    >
      <div
        className="w-full max-w-[520px] bg-surface border border-edge shadow-overlay rounded-2xl p-7 animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[22px] font-bold text-text-primary leading-snug">
          {title}
        </h2>

        {description ? (
          <p className="mt-2.5 text-[15px] font-medium text-light-gray leading-relaxed">
            {description}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3 mt-7">
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="h-11 px-6 rounded-xl text-[15px] font-semibold text-text-primary bg-surface border border-edge hover:border-edge-strong hover:bg-overlay-hover transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelLabel}
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={clsx(
              "h-11 px-6 rounded-xl text-[15px] font-semibold text-white border border-white/10 shadow-lg",
              "hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300 cursor-pointer",
              "disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0",
              "flex items-center justify-center min-w-[120px]",
              tone === "danger"
                ? "bg-gradient-to-r from-red-500 to-red-600 shadow-red-500/20"
                : "bg-gradient-to-r from-accent to-accent-bright shadow-indigo-500/20"
            )}
          >
            {loading ? <Loader /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
